import AppButton from '@nxsys/components/AppButton';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Modal, Space } from 'antd';
import { useDispatch } from 'react-redux';
import StepCard from './StepCard';
import { ImageZohoLogo } from '@nxsys/assets/images';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { AppDispatch } from '@nxsys/store';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { showMessage } from '@nxsys/utils/message';

// Zoho Sign request statuses we branch on. Anything else means "still out for
// signature".
const ZOHO_COMPLETED = 'completed';
const ZOHO_EXPIRED = 'expired';
const ZOHO_DELETED = 'deleted';

// `verifyZoho` is polled rather than pushed, so a freshly-signed document can
// still report as unsigned. Retry a few times before giving up.
const MAX_VERIFY_RETRIES = 3;

interface VerifyContactProps {
  stepNumber: number;
  markedAsCompleted: boolean;
  /**
   * True for an admin completing the onboarding on a prospect's behalf: the
   * Zoho endpoints are scoped to the signed-in user, so the admin can only view
   * the status and tick "Mark as completed". The employee flow drives the real
   * signing.
   */
  disabledConnect: boolean;
  zohoSignStatus?: string | null;
  zohoRequestId?: string | null;
  zohoDocumentId?: string | null;
  zohoDocumentExpired?: boolean | null;
  zohoDocumentDeleted?: boolean | null;
  /** Onboarding is in progress — used to auto-advance an already-signed step. */
  isProgress?: boolean;
  completed?: boolean;
  onSubmitStep: (values: any, options?: { hideToast?: boolean }) => void;
}

const VerifyContact: React.FC<VerifyContactProps> = ({
  stepNumber,
  markedAsCompleted = false,
  completed = false,
  disabledConnect = false,
  zohoSignStatus,
  zohoRequestId,
  zohoDocumentId,
  zohoDocumentExpired,
  zohoDocumentDeleted,
  isProgress = false,
  onSubmitStep,
}) => {
  const { messages } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const canSign = !disabledConnect;

  const [isMarkedAsCompleted, setIsMarkedAsCompleted] =
    useState(markedAsCompleted);
  const [zohoStatus, setZohoStatus] = useState<string | null>(
    zohoSignStatus ?? null,
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isExpiredOpen, setIsExpiredOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // Guards the one-shot status check on mount.
  const checkedOnMount = useRef(false);

  const isSigned =
    String(zohoStatus ?? '').toLowerCase() === ZOHO_COMPLETED ||
    String(zohoSignStatus ?? '').toLowerCase() === ZOHO_COMPLETED;

  const text = (key: string, fallback: string) =>
    (messages[key] ?? fallback) as string;

  const toastSigned = () =>
    showMessage({
      type: 'success',
      content: text(
        'prospect.onboarding.contractSigned',
        'Contract signed successfully!',
      ),
    });

  const toastZohoError = (error?: any) =>
    showMessage({
      type: 'error',
      content:
        error?.response?.data?.message ??
        error?.message ??
        text(
          'prospect.onboarding.zohoConnectionFailed',
          'ZOHO connection failed. Please process again ZOHO sign contract and sign the newest contract sent to your email account!',
        ),
    });

  const onSubmit = () => onSubmitStep({});

  const readZoho = (res: any) => {
    const payload = res?.data?.data ?? res?.data ?? {};
    return {
      status: String(payload?.status ?? '').toLowerCase(),
      expired: !!payload?.zohoDocumentExpired,
      deleted: !!payload?.zohoDocumentDeleted,
    };
  };

  /** Reads the current Zoho status and routes the user to the right modal. */
  const getContractStatus = async () => {
    try {
      const res = await dispatch(appActions.getStatusZoho()).unwrap();
      const { status, expired, deleted } = readZoho(res);
      setZohoStatus(status);

      if (status === ZOHO_COMPLETED) {
        setIsConfirmOpen(false);
        setIsExpiredOpen(false);
        toastSigned();
        return;
      }
      if (status === ZOHO_EXPIRED || status === ZOHO_DELETED || expired || deleted) {
        setIsExpiredOpen(true);
        showMessage({
          type: 'warning',
          content: text(
            'prospect.onboarding.contractExpired',
            'Your contract has expired. Please reconnect via Zoho Sign to continue',
          ),
        });
        return;
      }
      setIsConfirmOpen(true);
      showMessage({
        type: 'warning',
        content: text(
          'prospect.onboarding.contractNotCompleted',
          'Your contract signing has not been completed. Please finish signing your contract to continue',
        ),
      });
    } catch (error) {
      toastZohoError(error);
    }
  };

  /**
   * Confirms the signature with Zoho. Retries because the document can take a
   * moment to register as signed; after the last attempt it tells the user the
   * signing is still outstanding rather than failing silently.
   */
  const confirmContract = async (retry = 0) => {
    try {
      setVerifying(true);
      const res = await dispatch(appActions.verifyZoho()).unwrap();
      const { status, expired, deleted } = readZoho(res);
      setZohoStatus(status);

      if (status === ZOHO_COMPLETED) {
        setIsConfirmOpen(false);
        toastSigned();
        onSubmit();
        return;
      }
      if (status === ZOHO_EXPIRED || status === ZOHO_DELETED || expired || deleted) {
        setIsConfirmOpen(false);
        setIsExpiredOpen(true);
        return;
      }
      if (retry + 1 < MAX_VERIFY_RETRIES) {
        await confirmContract(retry + 1);
        return;
      }
      showMessage({
        type: 'warning',
        content: text(
          'prospect.onboarding.contractNotCompleted',
          'Your contract signing has not been completed. Please finish signing your contract to continue',
        ),
      });
    } catch (error) {
      toastZohoError(error);
      setIsConfirmOpen(true);
    } finally {
      setVerifying(false);
    }
  };

  /** Asks Zoho to (re)send the contract email. */
  const onSendMail = async () => {
    if (sending) return;
    try {
      setSending(true);
      await dispatch(appActions.onboardingSendMailZoho()).unwrap();
      setIsExpiredOpen(false);
      setIsConfirmOpen(true);
      showMessage({
        type: 'success',
        content: text(
          'prospect.onboarding.zohoEmailSent',
          'Zoho has sent you an email. Please check your inbox to continue',
        ),
      });
    } catch (error) {
      toastZohoError(error);
    } finally {
      setSending(false);
    }
  };

  const confirmThen = (content: string, onOk: () => void) => {
    Modal.confirm({
      title: text('common.confirm', 'Confirm'),
      content,
      okText: text('common.yes', 'Yes'),
      cancelText: text('common.no', 'No'),
      onOk,
    });
  };

  /** "Connect ZOHO sign contract" — asks before sending a new contract email. */
  const showConfirmSend = () => {
    if (isSigned) {
      toastSigned();
      return;
    }
    confirmThen(
      text(
        'prospect.onboarding.zohoSendConfirm',
        'Send the contract to your email via Zoho Sign?',
      ),
      onSendMail,
    );
  };

  // On mount, reconcile with Zoho: an expired or deleted document needs
  // resending, and an in-flight one needs its status checked.
  useEffect(() => {
    if (!canSign || checkedOnMount.current) return;
    setZohoStatus(zohoSignStatus ?? null);
    if (String(zohoSignStatus ?? '').toLowerCase() === ZOHO_COMPLETED) return;

    checkedOnMount.current = true;
    if (zohoDocumentExpired || zohoDocumentDeleted) {
      setIsExpiredOpen(true);
      return;
    }
    if (zohoRequestId && zohoDocumentId) {
      getContractStatus();
    }
  }, [canSign, zohoRequestId, zohoDocumentId]);

  // Already signed while the step is still open — advance without making the
  // user press anything.
  useEffect(() => {
    if (
      canSign &&
      isProgress &&
      String(zohoSignStatus ?? '').toLowerCase() === ZOHO_COMPLETED &&
      !completed
    ) {
      onSubmit();
    }
  }, [canSign, isProgress, zohoSignStatus]);

  const handleMarkAsCompleted = (e: CheckboxChangeEvent) => {
    if (!e.target.checked) return;
    setIsMarkedAsCompleted(true);
    onSubmitStep({});
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.verifyContract'] as string}
      stepNumber={stepNumber}
      completed={completed}
      headerRight={
        // Admin-only override: an admin cannot drive the Zoho signing, so they
        // tick the step off once the contract is signed elsewhere. The employee
        // has to actually sign, so the checkbox would let them skip the step.
        canSign ? undefined : (
          <AppCheckbox
            size="small"
            checked={isMarkedAsCompleted}
            disabled={isMarkedAsCompleted}
            onChange={handleMarkAsCompleted}
          >
            {messages['todo.markAsCompleted'] as string}
          </AppCheckbox>
        )
      }
    >
      <Alert
        className="mb-3"
        message={
          isSigned
            ? (messages['prospect.onboarding.contractSigned'] as string)
            : (messages['prospect.onboarding.contractPending'] as string)
        }
        type={isSigned ? 'success' : 'warning'}
        showIcon
      />
      <AppButton
        name={messages['prospect.onboarding.connectZohoSignContract'] as string}
        disabled={disabledConnect || sending}
        loading={sending}
        leftIcon={
          <img
            src={ImageZohoLogo}
            alt="zoho"
            style={{ width: 'unset', height: 'unset' }}
          />
        }
        onClick={canSign ? showConfirmSend : undefined}
      />

      {/* Contract is out for signature — confirm it, or send it again. */}
      <AppModalContainer
        isOpen={isConfirmOpen}
        toggle={() => setIsConfirmOpen(false)}
        title={text(
          'prospect.onboarding.zohoConfirmTitle',
          'Sign your contract',
        )}
        width={480}
      >
        <p className="mb-4">
          {text(
            'prospect.onboarding.zohoConfirmBody',
            'Zoho has emailed you the contract. Once you have signed it, confirm below.',
          )}
        </p>
        <Space>
          <AppButton
            name={text(
              'prospect.onboarding.confirmSignContract',
              'Confirm sign contract',
            )}
            loading={verifying}
            disabled={verifying}
            onClick={() =>
              confirmThen(
                text(
                  'prospect.onboarding.zohoFinishedConfirm',
                  'Did you finish contract signing?',
                ),
                () => confirmContract(0),
              )
            }
          />
          <AppButton
            variant="outlined"
            name={text('prospect.onboarding.resendContract', 'Resend contract')}
            loading={sending}
            disabled={sending}
            onClick={() =>
              confirmThen(
                text(
                  'prospect.onboarding.zohoResendConfirm',
                  'Send the contract again?',
                ),
                onSendMail,
              )
            }
          />
        </Space>
      </AppModalContainer>

      {/* Contract expired or was deleted in Zoho — only resending helps. */}
      <AppModalContainer
        isOpen={isExpiredOpen}
        toggle={() => setIsExpiredOpen(false)}
        title={text(
          'prospect.onboarding.zohoExpiredTitle',
          'Contract expired',
        )}
        width={480}
      >
        <p className="mb-4">
          {text(
            'prospect.onboarding.contractExpired',
            'Your contract has expired. Please reconnect via Zoho Sign to continue',
          )}
        </p>
        <AppButton
          name={text('prospect.onboarding.resendContract', 'Resend contract')}
          loading={sending}
          disabled={sending}
          onClick={onSendMail}
        />
      </AppModalContainer>
    </StepCard>
  );
};

export default VerifyContact;
